import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { LanguageConfigObject } from 'src/app/features/chat-bot/types/chat-flow.interface';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'language-switcher',
  templateUrl: './language-switcher.component.html',
  standalone: false,
  styleUrls: ['./language-switcher.component.scss']
})
export class LanguageSwitcherComponent implements OnInit {
  @Input() languageObject: LanguageConfigObject | undefined;
  @Output() currentLanguageChanged = new EventEmitter<string>();
  @Input() defaultLanguage: string | undefined;

  browserLanguage: string | undefined;
  languageToInit: string | undefined;

  constructor(private translateService: TranslateService) {}
  ngOnInit(): void {
    this.languageToInit = this.getInitialLanguage();
    setTimeout(() => {
      this.initLanguage(this.languageToInit);
    });
  }

  /**
   * Determines the initial language to use.
   * Priority: LocalStorage > Browser Language > Default Language
   */
  private getInitialLanguage(): string {
    let storedLanguage: string | null = null;
    try {
      storedLanguage = localStorage.getItem('user_lang');
    } catch {
      // Safari throws a SecurityError when storage is blocked (private browsing,
      // "Block All Cookies") - fall back to browser/default language
    }

    if (
      storedLanguage &&
      this.languageObject?.available_languages.includes(storedLanguage)
    ) {
      return storedLanguage;
    }

    this.browserLanguage = this.getBrowserLanguage();
    if (
      this.languageObject?.available_languages.includes(this.browserLanguage)
    ) {
      return this.browserLanguage;
    }

    return this.defaultLanguage ?? 'en'; // Fallback to 'en' if no default language is provided
  }

  /**
   * Initializes the language for the application.
   * @param selectedLang The language to initialize
   */
  private initLanguage(selectedLang?: string) {
    if (selectedLang) {
      this.translateService.setDefaultLang(selectedLang);
      this.translateService.use(selectedLang);
      this.updateHtmlLang(selectedLang);
      this.currentLanguageChanged.emit(selectedLang);
    }
  }

  /**
   * Handles user selection of a language.
   * @param selectedLanguage The language selected by the user
   */
  onSelectLanguage(selectedLanguage: string) {
    // Setting localStorage language on user selection
    try {
      localStorage.setItem('user_lang', selectedLanguage);
    } catch {
      // Storage blocked - the choice just won't survive a reload
    }
    this.languageToInit = selectedLanguage;
    this.updateHtmlLang(selectedLanguage);
    this.currentLanguageChanged.emit(selectedLanguage);
  }

  private updateHtmlLang(lang?: string): void {
    if (lang) {
      document.documentElement.lang = lang;
    }
  }

  private getBrowserLanguage(): string {
    return (navigator.language || navigator.languages[0]).substring(0, 2);
  }
}
